/*
Copyright 2023 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License.
*/
import { noop, sanitizeOrgIdForCookieName } from "../../utils/index.js";
import createOnResponseHandler from "./createOnResponseHandler.js";
import createDecisionProvider from "./createDecisionProvider.js";
import createApplyResponse from "./createApplyResponse.js";
import createEventRegistry from "./createEventRegistry.js";
import createContextProvider from "./createContextProvider.js";
import createSubscribeRulesetItems from "./createSubscribeRulesetItems.js";
import { CONTEXT_KEY, CONTEXT_EVENT_SOURCE, CONTEXT_EVENT_TYPE } from "./constants/index.js";
import createEvaluateRulesetsCommand from "./createEvaluateRulesetsCommand.js";
import { clearLocalStorage, createInMemoryStorage } from "./utils/storage.js";
import { objectOf, boolean } from "../../utils/validation/index.js";
import createCollect from "../../utils/createCollect.js";
import { mergeDecisionsMeta } from "../../utils/event.js";
const createRulesEngine = ({
  config,
  eventManager,
  createNamespacedStorage,
  consent,
  getBrowser,
  logger
}) => {
  const {
    orgId,
    personalizationStorageEnabled
  } = config;
  const collect = createCollect({
    eventManager,
    mergeDecisionsMeta
  });
  const storage = createNamespacedStorage(`${sanitizeOrgIdForCookieName(orgId)}.decisioning.`);
  if (!personalizationStorageEnabled) {
    clearLocalStorage(storage.persistent);
  }
  const eventRegistry = createEventRegistry({
    storage: createInMemoryStorage(),
    logger
  });
  const decisionProvider = createDecisionProvider({
    eventRegistry
  });
  const contextProvider = createContextProvider({
    eventRegistry,
    window,
    getBrowser
  });
  const evaluateRulesetsCommand = createEvaluateRulesetsCommand({
    contextProvider,
    decisionProvider
  });
  const subscribeRulesetItems = createSubscribeRulesetItems({
    collect
  });
  let applyResponse;
  return {
    lifecycle: {
      onDecision({
        propositions
      }) {
        subscribeRulesetItems.refresh(propositions);
      },
      onComponentsRegistered(tools) {
        applyResponse = createApplyResponse({
          lifecycle: tools.lifecycle,
          eventRegistry
        });
        if (personalizationStorageEnabled) {
          consent.awaitConsent().then(() => {
            eventRegistry.setStorage(storage.persistent);
          }).catch(() => {
            if (storage) {
              clearLocalStorage(storage.persistent);
            }
          });
        }
      },
      onBeforeEvent({
        event,
        renderDecisions,
        personalization = {},
        onResponse = noop
      }) {
        const {
          decisionContext = {}
        } = personalization;
        onResponse(createOnResponseHandler({
          renderDecisions,
          decisionProvider,
          applyResponse,
          event,
          personalization,
          decisionContext: contextProvider.getContext({
            [CONTEXT_KEY.TYPE]: CONTEXT_EVENT_TYPE.EDGE,
            [CONTEXT_KEY.SOURCE]: CONTEXT_EVENT_SOURCE.REQUEST,
            ...decisionContext
          })
        }));
      },
      onBeforeRequest({
        request
      }) {
        const payload = request.getPayload().toJSON();
        const {
          events = []
        } = payload;
        if (events.length === 0) {
          return;
        }
        events.forEach(event => eventRegistry.addExperienceEdgeEvent(event));
      }
    },
    commands: {
      evaluateRulesets: {
        run: ({
          renderDecisions,
          personalization = {}
        }) => {
          const {
            decisionContext = {}
          } = personalization;
          return evaluateRulesetsCommand.run({
            renderDecisions,
            decisionContext: {
              [CONTEXT_KEY.TYPE]: CONTEXT_EVENT_TYPE.RULES_ENGINE,
              [CONTEXT_KEY.SOURCE]: CONTEXT_EVENT_SOURCE.REQUEST,
              ...decisionContext
            },
            applyResponse
          });
        },
        optionsValidator: evaluateRulesetsCommand.optionsValidator
      },
      subscribeRulesetItems: subscribeRulesetItems.command
    }
  };
};
createRulesEngine.namespace = "RulesEngine";
createRulesEngine.configValidators = objectOf({
  personalizationStorageEnabled: boolean().default(false)
});
export default createRulesEngine;